import { useCallback } from 'react'
import { FieldValues, Path, UseFormReturn } from 'react-hook-form'
import { toast } from 'react-toastify'

import { AUTH, MessageType, type ErrorHandler } from '@/shared/api'
import { useCommonDialog } from '@/shared/lib'
import { ErrorDialogProps, useErrorDialogStore } from '@/shared/store'

const isErrorHandler = (handler: unknown): handler is ErrorHandler => {
  return (
    !!handler &&
    typeof handler === 'object' &&
    'code' in handler &&
    'type' in handler &&
    'content' in handler &&
    [MessageType.TOAST, MessageType.ALERT, MessageType.CONFIRM].includes(
      handler.type as MessageType,
    )
  )
}

export const useServerErrorHandler = <T extends FieldValues>(
  form?: UseFormReturn<T>,
) => {
  const { openMoveToSignInDialog } = useCommonDialog()
  const { actions } = useErrorDialogStore()

  const handleServerError = useCallback(
    (handler: unknown, dialogProps: Partial<ErrorDialogProps> = {}) => {
      if (!isErrorHandler(handler)) return

      if (handler.code === AUTH['002']) {
        openMoveToSignInDialog()

        return
      }

      if (form && handler.data) {
        Object.entries(handler.data).forEach(([field, message]) => {
          form?.setError(field as Path<T>, { message })
        })

        return
      }

      const { type, content, title, onConfirm } = handler

      if (type === MessageType.TOAST) {
        toast(handler.content)
      } else {
        actions.open({
          title,
          content,
          type,
          onConfirm,
          ...dialogProps,
        })
      }
    },
    [form, actions],
  )

  return { handleServerError }
}
